// Mobile pieces for the Automations hub (mobile overhaul Phase D §14.1):
// the knob steppers that replace the desktop number inputs, the send-history
// half sheet, the template editor's preview sheet, and the discard confirm
// that stands in for window.confirm. All presentation — every call the page
// makes stays in pages/Automations.tsx.

import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetButton } from './Sheet';
import { Pill, Seg, type PillTone } from './primitives';
import { knobRangeLabel, knobStepDisabled, stepKnob, type KnobRange } from '../lib/automationKnobs';

// ── Knob stepper (frame A) ───────────────────────────────────────────────

/** − / + around the value, with the unit and range under the label. The
    shown value moves at once and re-syncs from the server's reply; a failed
    PATCH puts the old value back, exactly like the desktop input's commit. */
export function KnobStepper({
  label,
  unit,
  range,
  value,
  busy,
  onSave,
}: {
  label: string;
  unit?: string;
  range: KnobRange;
  value: number;
  busy: boolean;
  onSave: (v: number) => Promise<void>;
}) {
  const [shown, setShown] = useState(value);
  useEffect(() => setShown(value), [value]);

  const step = (direction: -1 | 1) => {
    const next = stepKnob(shown, direction, range);
    if (next === shown) return;
    setShown(next);
    onSave(next).catch(() => setShown(value));
  };

  return (
    <div className="atm-m-knob">
      <span className="k">
        {label}
        <small>{knobRangeLabel(unit, range)}</small>
      </span>
      <span className="stp">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={busy || knobStepDisabled(shown, -1, range)}
          onClick={() => step(-1)}
        >
          −
        </button>
        <b aria-live="polite">{shown}</b>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          disabled={busy || knobStepDisabled(shown, 1, range)}
          onClick={() => step(1)}
        >
          +
        </button>
      </span>
    </div>
  );
}

// ── Send history (frame B) ───────────────────────────────────────────────

export interface AutomationHistoryItem {
  id: string;
  subject: string;
  to: string;
  when: string;
  error: string | null;
  statusLabel: string;
  tone: PillTone;
  projectId: string | null;
}

const HISTORY_PAGE = 5;

export function AutomationHistorySheet({
  open,
  onClose,
  title,
  items,
}: {
  open: boolean;
  onClose: () => void;
  /** The automation's name — the sheet's mono eyebrow. */
  title: string;
  items: AutomationHistoryItem[];
}) {
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (open) setShowAll(false);
  }, [open]);

  const visible = showAll ? items : items.slice(0, HISTORY_PAGE);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      variant="half"
      title="Recent sends"
      context={items.length === 0 ? `${title} · none yet` : `${title} · ${visible.length} of ${items.length}`}
      rightAction={<SheetButton onClick={onClose}>Close</SheetButton>}
      className="atm-m-history"
    >
      {items.length === 0 && <div className="atm-m-hempty">Nothing sent yet.</div>}
      {visible.map((item) => {
        const body: ReactNode = (
          <>
            <span className="atm-m-hmain">
              <span className="atm-m-hsubject">{item.subject}</span>
              <span className="atm-m-hmeta">
                {item.to}
                <span className="sep">·</span>
                {item.when}
              </span>
              {item.error && <span className="atm-m-herr">{item.error}</span>}
            </span>
            <Pill tone={item.tone}>{item.statusLabel}</Pill>
          </>
        );
        return item.projectId ? (
          <Link className="atm-m-hrow" key={item.id} to={`/projects/${item.projectId}`} onClick={onClose}>
            {body}
          </Link>
        ) : (
          <div className="atm-m-hrow" key={item.id}>
            {body}
          </div>
        );
      })}
      {!showAll && items.length > HISTORY_PAGE && (
        <button type="button" className="atm-m-hmore" onClick={() => setShowAll(true)}>
          Show all {items.length} →
        </button>
      )}
    </Sheet>
  );
}

// ── Template editor sheets (frame C) ─────────────────────────────────────

/** The body textarea grows with its content instead of scrolling inside
    itself — a fixed box on a phone hides most of a real email. */
export function AutoGrowTextarea({
  innerRef,
  value,
  onChange,
  onFocus,
  className,
}: {
  innerRef: RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  className?: string;
}) {
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [innerRef, value]);

  return (
    <textarea
      ref={innerRef}
      className={className}
      value={value}
      onFocus={onFocus}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function TemplatePreviewSheet({
  open,
  onClose,
  mode,
  onMode,
  from,
  to,
  sampleLabel,
  subject,
  html,
  plainText,
}: {
  open: boolean;
  onClose: () => void;
  mode: 'desktop' | 'plaintext';
  onMode: (m: 'desktop' | 'plaintext') => void;
  from: string;
  to: string;
  sampleLabel?: string;
  subject: string;
  /** Null while the preview endpoint is unavailable — the draft body shows instead. */
  html: string | null;
  plainText: string;
}) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      variant="full"
      title="Preview"
      context="Sample data"
      leftAction={<SheetButton onClick={onClose}>Back</SheetButton>}
      className="atm-m-preview"
      pinned={
        <div className="atm-m-preview-seg">
          <Seg
            options={[
              { value: 'desktop', label: 'Desktop' },
              { value: 'plaintext', label: 'Plain-text' },
            ]}
            value={mode}
            onChange={onMode}
          />
        </div>
      }
    >
      {mode === 'desktop' ? (
        <div className="atm-m-email">
          <div className="atm-m-email-head">
            <span>
              <b>From:</b> {from}
            </span>
            <span>
              <b>To:</b> {to}
            </span>
            {sampleLabel && <span className="sample">Sample: {sampleLabel}</span>}
          </div>
          <div className="atm-m-email-subject">{subject}</div>
          {html !== null ? (
            <iframe className="atm-m-email-frame" srcDoc={html} sandbox="" title="Template preview" />
          ) : (
            <div className="atm-m-email-fallback">{plainText}</div>
          )}
        </div>
      ) : (
        <div className="atm-m-plaintext">{plainText}</div>
      )}
    </Sheet>
  );
}

/** Cancel with unsaved edits — the sheet-native stand-in for window.confirm,
    shaped like every other confirm sheet in the app (Phase B). */
export function DiscardChangesSheet({
  open,
  context,
  onKeep,
  onDiscard,
}: {
  open: boolean;
  /** The automation whose template is being edited. */
  context: string;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  return (
    <Sheet
      open={open}
      onClose={onKeep}
      variant="half"
      title="Discard unsaved changes?"
      context={context}
      footer={
        <>
          <button type="button" className="btn" onClick={onKeep}>
            Keep editing
          </button>
          <button type="button" className="btn danger" onClick={onDiscard}>
            Discard
          </button>
        </>
      }
    >
      <p className="atm-m-discard">Edits to this template haven’t been saved yet.</p>
    </Sheet>
  );
}
